import { useState } from 'react';
import {
  Building2,
  Loader2,
  ArrowRight,
  RefreshCw,
  Users,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useUserProfile } from '../context/UserProfileContext';
import { api } from '../lib/api';

export function NotInvitedPage() {
  const { signOut, user } = useAuth();
  const { refreshMemberships } = useUserProfile();

  const [refreshing, setRefreshing] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshMemberships();
    setRefreshing(false);
  };

  const handleCreateOrg = async () => {
    if (!companyName.trim()) return;

    setCreating(true);
    setCreateError(null);

    try {
      await api.post<{ success: boolean; data: { orgId: string } }>(
        '/onboarding/create-org',
        { name: companyName.trim() },
      );
      await refreshMemberships();
      // AuthenticatedRoutes will now see memberships.length > 0
      // and redirect to ChoosePlanPage (no subscription yet)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create organization.';
      setCreateError(message);
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-mist flex flex-col items-center justify-start pt-10 px-4 pb-12">
      <div className="w-full max-w-md flex flex-col gap-6">

        {/* Header */}
        <div className="text-center flex flex-col gap-3">
          <div className="w-16 h-16 rounded-full bg-plum/10 flex items-center justify-center mx-auto">
            <Building2 size={28} className="text-plum" />
          </div>
          <h1 className="font-display text-h1 text-midnight leading-tight">
            Welcome to IOMI Pro
          </h1>
          <p className="text-body text-slate">
            <span className="font-medium text-midnight">{user?.email}</span> isn't
            part of any organization yet.
          </p>
        </div>

        {/* Already part of a team? */}
        <div className="bg-white rounded-xl border border-stone px-5 py-4 shadow-brand-sm flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-plum shrink-0" />
            <p className="text-sm font-semibold text-midnight">Already part of a team?</p>
          </div>
          <p className="text-sm text-slate leading-relaxed">
            Ask your organization's admin to invite{' '}
            <span className="font-medium text-midnight">{user?.email}</span>.
            Once they add you, tap Refresh below.
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary w-full mt-1 flex items-center justify-center gap-2"
          >
            {refreshing ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Checking…
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Refresh
              </>
            )}
          </button>
        </div>

        {/* Create New Company Account — big gradient button */}
        <button
          type="button"
          onClick={() => { setShowModal(true); setCreateError(null); setCompanyName(''); }}
          className="w-full h-14 rounded-xl
                     bg-gradient-to-r from-plum to-rose
                     text-white text-[16px] font-bold tracking-wide
                     flex items-center justify-center gap-2.5
                     shadow-lg shadow-plum/30
                     hover:shadow-xl hover:shadow-plum/40
                     active:opacity-90 active:scale-[0.98]
                     transition-all duration-200"
        >
          <Building2 size={20} />
          Create New Company Account
        </button>

        {/* Sign out */}
        <button
          type="button"
          onClick={() => signOut()}
          className="w-full py-3 text-small text-slate hover:text-midnight transition-colors text-center"
        >
          Sign Out
        </button>
      </div>

      {/* ── Create Company Modal ──────────────────────────────────────── */}
      {showModal && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-midnight/70"
            onClick={() => { if (!creating) setShowModal(false); }}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 flex flex-col gap-4">
            <div>
              <h2 className="font-display text-h2 font-semibold text-midnight">
                Create Your Company
              </h2>
              <p className="text-sm text-slate mt-1 leading-relaxed">
                Enter your moving company's name to get started. You'll choose a plan next.
              </p>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Company Name <span className="text-rose">*</span>
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Acme Moving Co."
                autoFocus
                disabled={creating}
                onKeyDown={(e) => e.key === 'Enter' && companyName.trim() && handleCreateOrg()}
                className="w-full h-11 px-3 rounded-lg border border-stone
                           bg-white text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors disabled:opacity-50"
              />
            </div>

            {createError && (
              <div className="flex items-start gap-2 text-small text-red-600">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{createError}</span>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={creating}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateOrg}
                disabled={creating || !companyName.trim()}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    Get Started
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
